import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';

interface UnpublishReviewParams {
  reviewId: number;
  moderationNotes: string;
}

/**
 * Composable for unpublishing a review
 * Performs optimistic updates to remove the review from published list
 */
export function useUnpublishReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['admin', 'unpublishReview'],
    mutationFn: async ({ reviewId, moderationNotes }: UnpublishReviewParams) => {
      return await api.admin.reviewsV2.unpublishReview({
        params: { review: reviewId },
        body: { moderation_notes: moderationNotes },
      });
    },
    onMutate: async ({ reviewId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['admin', 'reviews', 'published'] });
      await queryClient.cancelQueries({ queryKey: ['admin', 'reviews', 'all'] });

      // Snapshot the previous values
      const previousPublished = queryClient.getQueryData(['admin', 'reviews', 'published']);
      const previousAll = queryClient.getQueryData(['admin', 'reviews', 'all']);

      // Optimistically update the caches
      queryClient.setQueryData(['admin', 'reviews', 'published'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: page.data.filter((review: any) => review.id !== reviewId),
            meta: page.meta
              ? {
                  ...page.meta,
                  total_published: Math.max(0, (page.meta.total_published || 0) - 1),
                }
              : page.meta,
          })),
        };
      });

      return { previousPublished, previousAll };
    },
    onError: (error: any, _variables, context) => {
      // Revert the optimistic updates on error
      if (context?.previousPublished) {
        queryClient.setQueryData(['admin', 'reviews', 'published'], context.previousPublished);
      }
      if (context?.previousAll) {
        queryClient.setQueryData(['admin', 'reviews', 'all'], context.previousAll);
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to unpublish review. Please try again.';

      toaster.create({
        title: 'Error unpublishing review',
        description: errorMessage,
        type: 'error',
      });

      console.error('[useUnpublishReview] Error:', error);
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review unpublished successfully',
        description: 'The review is no longer visible to the public.',
        type: 'success',
      });
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'published'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews', 'all'] });
    },
  });

  return {
    unpublishReview: mutation.mutate,
    unpublishReviewAsync: mutation.mutateAsync,
    isUnpublishing: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
}

